<template>
  <EmbedPlayer
    ref="embedPlayerRef"
    :embed-id="assetId"
    :asset-type="assetType"
    :preset="preset"
    :auto-play="autoPlay"
    :auto-play-next="autoPlayNext"
    :show-ads="showAds"
    :loop="loop"
    :show-playlist="showPlaylist"
    :collection-display-mode="collectionDisplayMode"
    :enable-download="enableDownload"
    :show-chapters="showChapters"
    :enable-pip="enablePip"
    :enable-fullscreen="enableFullscreen"
    :shuffle="shuffle"
    :show-waveform="showWaveform"
    :enable-zoom="enableZoom"
    :show-thumbnails="showThumbnails"
    :show-outline="showOutline"
    :poster-fit="posterFit"
    :muted="muted"
    :controls="controls"
    :pin-controls="pinControls"
    :seeking="seeking"
    :show-homepage-info="showHomepageInfo"
    :show-homepage-play-button="showHomepagePlayButton"
    :show-eco-mode="showEcoMode"
    :eco-mode-default="ecoModeDefault"
    :show-info-button="showInfoButton"
    :start-at="startAt"
    :enable-slideshow="enableSlideshow"
    :slideshow-duration="slideshowDuration"
    :show-captions="showCaptions"
    :start-page="startPage"
    :enable-search="enableSearch"
    :theme="theme"
    :icon-color="iconColor"
    :primary-color="primaryColor"
    :enable-asset-link="enableAssetLink"
    :show-logo="showLogo"
    :logo-url="logoUrl"
    :logo-position="logoPosition"
    :logo-click-url="logoClickUrl"
    :custom-css="customCss"
    @embed-load="$emit('embedLoad', $event)"
    @item-change="(index: number, item: unknown) => $emit('itemChange', index, item)"
    @play="$emit('play')"
    @pause="$emit('pause')"
    @ended="$emit('ended')"
    @error="$emit('error', $event)"
    @time-update="$emit('timeUpdate', $event)"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Embed } from '../types/embed'
import type { PlayerError } from '../types/base'
import type { CollectionDisplayMode } from '../video/types'
import { provideEmbedLoaderApi } from '../common/composables/usePlayerContext'
import { loadAppPlayer } from '../common/composables/useAssetPlayer'
import EmbedPlayer from './EmbedPlayer.vue'

interface Props {
  /** Asset ID to load */
  assetId: string
  /** Asset type hint — if provided and not playable, skips the API call */
  assetType?: string
  /**
   * A preset to preview the asset with, first in the cascade (`asset_player_read`'s `preset`) —
   * read once with the asset, so a caller that changes it remounts the player.
   */
  preset?: string
  /** Auto-play when loaded */
  autoPlay?: boolean
  /** Auto-play next item in a collection when the current one ends */
  autoPlayNext?: boolean
  /** Play ads configured on the embed (pre-roll/mid-roll/post-roll) */
  showAds?: boolean
  /** Loop playback */
  loop?: boolean
  /** Show playlist for collections */
  showPlaylist?: boolean
  /** Enable download */
  enableDownload?: boolean
  /** Collection display mode */
  collectionDisplayMode?: CollectionDisplayMode
  /** Show chapter markers (video) */
  showChapters?: boolean
  /** Show eco mode button (video) */
  showEcoMode?: boolean
  /** Start in eco mode; undefined defers to the embed response */
  ecoModeDefault?: boolean
  /** Show info button (video) */
  showInfoButton?: boolean
  /** Enable picture-in-picture (video) */
  enablePip?: boolean
  /** Enable fullscreen (video, audio, image) */
  enableFullscreen?: boolean
  /** Shuffle mode (audio) */
  shuffle?: boolean
  /** Show waveform visualization (audio) */
  showWaveform?: boolean
  /** Enable zoom (image) */
  enableZoom?: boolean
  /** Show thumbnail sidebar (document) */
  showThumbnails?: boolean
  /** Show outline sidebar (document) */
  showOutline?: boolean
  /** How the poster thumbnail fits the player area (video) */
  posterFit?: 'contain' | 'cover'
  /** Start muted (video) */
  muted?: boolean
  /** Show player controls (video) */
  controls?: boolean
  /** Keep the control bar always visible (video, disables auto-hide) */
  pinControls?: boolean
  /** Whether the viewer may move the playhead (video, audio) — a code's `seeking`, previewed */
  seeking?: boolean
  /** Show title/duration on homepage overlay (video) */
  showHomepageInfo?: boolean
  /** Show play button on homepage overlay (video) */
  showHomepagePlayButton?: boolean
  /** Start playback at this time in seconds (video, audio) */
  startAt?: number
  /** Enable slideshow for image collections (image) */
  enableSlideshow?: boolean
  /** Slideshow interval in seconds (image) */
  slideshowDuration?: number
  /** Show captions under images in grid layout (image) */
  showCaptions?: boolean
  /** Initial page number (document) */
  startPage?: number
  /** Enable text search (document) */
  enableSearch?: boolean
  /** Player theme */
  theme?: 'dark' | 'light' | 'colorful'
  /** Custom icon color (CSS color value, audio only) */
  iconColor?: string
  /** Primary color override (CSS color value) */
  primaryColor?: string
  /** Enable in-app asset link (image collections, Mediasbox only) */
  enableAssetLink?: boolean
  /** Logo to display when the caller holds one the embed API does not know yet */
  logoUrl?: string
  /** Show workspace logo overlay */
  showLogo?: boolean
  /** Override logo position */
  logoPosition?: 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'
  /** Override logo click URL */
  logoClickUrl?: string
  /** Custom CSS to inject into the player */
  customCss?: string
  /**
   * An asset the member reads in the organization's pool, when `assetId` is a file linked to it on a
   * shelf they do not see: the player is opened through it (`loadAppPlayer`).
   */
  viaAssetId?: string
  /**
   * Start on the asset's default subtitle track, as a publication by its own uid does. The asset's
   * preview asks for it; a working player — an overlay being placed, a settings or embed preview —
   * leaves it off, so nothing is drawn over the picture it is used to judge.
   */
  subtitlesAutoStart?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  assetType: undefined,
  preset: undefined,
  autoPlay: false,
  autoPlayNext: true,
  showAds: true,
  loop: false,
  showPlaylist: true,
  enableDownload: false,
  collectionDisplayMode: 'bottom-bar',
  showChapters: true,
  showEcoMode: true,
  ecoModeDefault: undefined,
  showInfoButton: true,
  enablePip: true,
  enableFullscreen: true,
  shuffle: false,
  showWaveform: true,
  enableZoom: true,
  showThumbnails: false,
  showOutline: false,
  posterFit: 'contain',
  muted: false,
  controls: true,
  pinControls: false,
  seeking: true,
  showHomepageInfo: true,
  showHomepagePlayButton: true,
  startAt: undefined,
  enableSlideshow: true,
  slideshowDuration: 5,
  showCaptions: true,
  startPage: 1,
  enableSearch: true,
  theme: 'colorful',
  iconColor: undefined,
  primaryColor: undefined,
  enableAssetLink: false,
  showLogo: true,
  logoUrl: undefined,
  logoPosition: undefined,
  logoClickUrl: undefined,
  customCss: undefined,
  viaAssetId: undefined,
  subtitlesAutoStart: false,
})

const emit = defineEmits<{
  embedLoad: [embed: Embed]
  itemChange: [index: number, item: unknown]
  play: []
  pause: []
  ended: []
  error: [error: PlayerError]
  timeUpdate: [currentTime: number]
}>()

// The in-app loader for every embed component below: a playback token from `app`, the asset read
// for what to list, and the `visitor` playback routes from there. Its failures are a member's.
provideEmbedLoaderApi(
  (id, params) => loadAppPlayer(id, params, props.viaAssetId, props.subtitlesAutoStart),
  'member',
)

const embedPlayerRef = ref<InstanceType<typeof EmbedPlayer> | null>(null)

defineExpose({
  embed: computed(() => embedPlayerRef.value?.embed),
  reload: () => embedPlayerRef.value?.reload(),
  goToIndex: (index: number) => embedPlayerRef.value?.goToIndex(index),
  play: () => (embedPlayerRef.value as any)?.play?.(),
  pause: () => (embedPlayerRef.value as any)?.pause?.(),
  seek: (time: number) => (embedPlayerRef.value as any)?.seek?.(time),
  startPlayback: () => (embedPlayerRef.value as any)?.startPlayback?.(),
  duration: computed(() => (embedPlayerRef.value as any)?.duration),
})
</script>
